/// <reference path="../../env.d.ts" />
import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  IconButton,
  MenuItem,
  Paper,
  useMediaQuery,
  Alert,
  LinearProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { useEditSite } from "../../utils/api/editSite.ts";
import { useAuth } from "../../context/auth.context.tsx";
import { useNotification } from "../../context/notification.context.tsx";
import { usePortfolios } from "../../utils/api/usePortfolios.ts";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { useSite, useSites } from "../../utils/api/useSites.ts";
import { refreshSiteAndGeometryCaches } from "../../utils/api/refreshSiteAndGeometryCaches.ts";
import { useSiteGeometry } from "../../utils/api/useSiteGeometry.ts";
import { useSiteGeometryGeoJSON } from "../../utils/api/useSiteGeometryGeoJSON.ts";
import PolygonMap from "../PolygonMap/PolygonMap.tsx";
import { isGeoJson, geojsonToWkt, polygonFromApi } from "../../utils/polygonUtils.ts";
import { useEditSiteGeometry } from "../../utils/api/editSiteGeometry.ts";
import { useResetSiteGeometry } from "../../utils/api/resetSiteGeometry.ts";
import useAxiosAuth from "../../utils/hooks/useAxiosAuth.ts";
import CustomButton from "../Basics/CustomButton/CustomButton.tsx";
import ConfirmResetGeometryModal from "./ConfirmResetGeometryModal.tsx";
import {
  validateSiteForm,
  parseBackendValidationErrors,
  toUserFriendlyPolygonError,
  hasDuplicateSamId,
  isSamIdUnchanged,
} from "../../utils/siteFormUtils.ts";

const REQUIRED_FIELDS = ["sam_id", "name", "portfolio", "dc_kw", "ac_kw"] as const;
const NUMERIC_FIELDS = ["dc_kw", "ac_kw"];

const isValidLatitude = (value: unknown) => {
  const latitude = Number(value);
  return Number.isFinite(latitude) && latitude >= -90 && latitude <= 90;
};

const isValidLongitude = (value: unknown) => {
  const longitude = Number(value);
  return Number.isFinite(longitude) && longitude >= -180 && longitude <= 180;
};

const haveCoordinatesChanged = (
  formLatitude: unknown,
  formLongitude: unknown,
  savedLatitude: unknown,
  savedLongitude: unknown
) =>
  Number(formLatitude) !== Number(savedLatitude) ||
  Number(formLongitude) !== Number(savedLongitude);

const getMapCenterFromSite = (
  latitude: unknown,
  longitude: unknown
): { lat: number; lng: number } | undefined => {
  const lat = parseFloat(String(latitude));
  const lng = parseFloat(String(longitude));
  return !isNaN(lat) && !isNaN(lng) ? { lat, lng } : undefined;
};

interface EditNewSiteModalProps {
  open: boolean;
  onClose: () => void;
  id: any;
}

const getServerMapPolygon = (geometry?: { poly?: unknown }) =>
  polygonFromApi(geometry?.poly);

const getServerPolygonWkt = (geometry?: { poly?: unknown }): string => {
  const raw = polygonFromApi(geometry?.poly);
  if (!raw) return "";
  if (isGeoJson(raw)) {
    try {
      return geojsonToWkt(JSON.parse(raw));
    } catch {
      return "";
    }
  }
  return raw;
};

/** Wider edit dialog; map column sizes are inline below */
const EDIT_SITE_MODAL_MAX_WIDTH = 1200;
const EDIT_SITE_MODAL_VH = 90;
/** Used in calc(...) so the two-column area fits under the sticky header */
const EDIT_SITE_BODY_BELOW_HEADER_PX = 80;
/** Slightly tighter cap for the scrollable form column */
const EDIT_SITE_FORM_SCROLL_TOP_PX = 72;

const EditNewSiteModal: React.FC<EditNewSiteModalProps> = ({
  open,
  onClose,
  id,
}) => {
  const theme = useTheme();
  const isWideLayout = useMediaQuery(theme.breakpoints.up("md"));

  const { mutate: editSite, isPending } = useEditSite();
  const { mutate: editSiteGeometry, isPending: isSavingGeometry } =
    useEditSiteGeometry();
  const { mutate: resetSiteGeometry, isPending: isResettingGeometry } =
    useResetSiteGeometry();
  const { session } = useAuth();
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();
  const axiosAuth = useAxiosAuth();
  const [mapRenderVersion, setMapRenderVersion] = useState(0);
  const [isRefreshingModalData, setIsRefreshingModalData] = useState(
    () => open && id != null && id !== ""
  );

  const { data: PortfoliosData, isLoading: isLoadingPortfoliosData } =
    usePortfolios(session);
  const {
    data: sitesData,
    isLoading: isLoadingSites,
    isError: isErrorSites,
  } = useSites(session);

  const siteQueriesEnabled = open && !isRefreshingModalData;

  const {
    data: siteData,
    isLoading: isLoadingSiteData,
    isFetching: isFetchingSiteData,
  } = useSite(session, id, siteQueriesEnabled);

  const {
    data: siteGeometry,
    isLoading: isLoadingSiteGeometry,
    isFetching: isFetchingSiteGeometry,
  } = useSiteGeometry(session, siteData?.sitegeometry, siteQueriesEnabled);

  const { data: siteGeometryGeoJSON } = useSiteGeometryGeoJSON(
    session,
    siteGeometry?.id
  );

  // Form data state
  const [formData, setFormData] = useState<any>({
    sam_id: "",
    name: "",
    city: "",
    state: "",
    address: "",
    latitude: "",
    longitude: "",
    portfolio: "",
    docket_id: "",
    dc_kw: "",
    ac_kw: "",
    polygon: "",
    ac_source: "",
    dc_source: "",
  });

  /** Initial polygon as WKT (avoids GeoJSON vs WKT mismatch) */
  const [initialPolygonWkt, setInitialPolygonWkt] = useState("");
  const [initialSamId, setInitialSamId] = useState("");
  const [polygonFieldEdited, setPolygonFieldEdited] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isResetGeometryModalOpen, setIsResetGeometryModalOpen] =
    useState(false);

  // Reset local form state when the modal closes.
  useEffect(() => {
    if (!open) {
      setFormData({
        sam_id: "",
        name: "",
        city: "",
        state: "",
        address: "",
        latitude: "",
        longitude: "",
        portfolio: "",
        docket_id: "",
        dc_kw: "",
        ac_kw: "",
        polygon: "",
        ac_source: "",
        dc_source: "",
      });
      setInitialPolygonWkt("");
      setInitialSamId("");
      setPolygonFieldEdited(false);
      setFormErrors({});
      setMapRenderVersion(0);
      setIsRefreshingModalData(false);
    }
  }, [open]);

  // Force-fetch site + geometry from API whenever the modal opens.
  useEffect(() => {
    if (!open || !id) return;

    let cancelled = false;
    setIsRefreshingModalData(true);

    void refreshSiteAndGeometryCaches(axiosAuth, queryClient, id).then(() => {
      if (cancelled) return;
      setIsRefreshingModalData(false);
      setMapRenderVersion((version) => version + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [open, id, axiosAuth, queryClient]);

  // Hydrate form from site + geometry (map reads geometry directly).
  useEffect(() => {
    if (
      !open ||
      !siteData ||
      !siteGeometry ||
      isLoadingSiteData ||
      isLoadingSiteGeometry
    ) {
      return;
    }

    const polygonWktForCompare = getServerPolygonWkt(siteGeometry);
    const polygonValue =
      siteGeometryGeoJSON && typeof siteGeometryGeoJSON === "object"
        ? JSON.stringify(siteGeometryGeoJSON, null, 2)
        : getServerMapPolygon(siteGeometry) || polygonWktForCompare;

    setFormData((prev) => ({
      sam_id: siteData.sam_id || "",
      name: siteData.name || "",
      city: siteData.city || "",
      state: siteData.state || "",
      address: siteData.address || "",
      latitude: siteData.latitude || "",
      longitude: siteData.longitude || "",
      portfolio: siteData.portfolio || "",
      docket_id: siteData.docket_id || "",
      dc_kw: siteData.dc_kw || "",
      ac_kw: siteData.ac_kw || "",
      polygon: polygonFieldEdited ? prev.polygon : polygonValue,
      ac_source: siteData.ac_source || "",
      dc_source: siteData.dc_source || "",
    }));

    if (!polygonFieldEdited) {
      setInitialPolygonWkt(polygonWktForCompare);
    }
    setInitialSamId(siteData.sam_id || "");
  }, [
    open,
    siteData,
    siteGeometry,
    siteGeometryGeoJSON,
    isLoadingSiteData,
    isLoadingSiteGeometry,
    polygonFieldEdited,
  ]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "polygon") {
      setPolygonFieldEdited(true);
    }
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "dc_kw" || name === "ac_kw" ? parseFloat(value) || "" : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = () => {
    const errors = validateSiteForm(formData, REQUIRED_FIELDS, NUMERIC_FIELDS);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (isLoadingSites || isErrorSites || !sitesData) {
      setFormErrors({
        sam_id: "Unable to verify this SAM ID is unique. Please try again.",
      });
      return;
    }

    const currentSiteId =
      id ?? siteData?.url?.match(/\/sites\/(\d+)\//)?.[1];

    if (
      !isSamIdUnchanged(formData.sam_id, initialSamId) &&
      hasDuplicateSamId(sitesData, formData.sam_id, currentSiteId)
    ) {
      setFormErrors({
        sam_id:
          "A site with this SAM ID already exists. Please enter a different SAM ID.",
      });
      return;
    }

    const polygonValue = formData.polygon || "";
    let polygonWkt: string;
    if (isGeoJson(polygonValue)) {
      try {
        const parsed = JSON.parse(polygonValue.trim());
        polygonWkt = geojsonToWkt(parsed);
        if (!polygonWkt) {
          setFormErrors((prev) => ({
            ...prev,
            polygon: "Invalid GeoJSON: could not convert to polygon",
          }));
          return;
        }
      } catch {
        setFormErrors((prev) => ({ ...prev, polygon: "Invalid GeoJSON" }));
        return;
      }
    } else {
      polygonWkt = polygonValue;
    }

    setFormErrors({});
    setIsSubmitting(true);

    const payload = { ...formData };
    payload.portfolio = `${process.env.REACT_APP_API_BASE_URL}portfolios/${formData.portfolio}/`;
    payload.dc_source = (payload.dc_source || "").replace(/ /g, "_");
    payload.ac_source = (payload.ac_source || "").replace(/ /g, "_");
    payload.polygon = polygonWkt;

    editSite(
      { ...payload, id },
      {
        onSuccess: () => {
          const polygonChanged = polygonWkt !== initialPolygonWkt;

          if (polygonChanged && payload.polygon) {
            const siteUrl =
              siteGeometry?.site ??
              `${process.env.REACT_APP_API_BASE_URL}sites/${id}/`;
            const isExistingSynthetic =
              Boolean(siteGeometry?.synthetic) ||
              Boolean(siteData?.sitegeometry_synthetic);

            editSiteGeometry(
              {
                id: siteGeometry?.id,
                site: siteUrl,
                siteId: id,
                poly: payload.polygon,
                isExistingSynthetic,
              },
              {
              onSuccess: async () => {
                setPolygonFieldEdited(false);
                await refreshSiteAndGeometryCaches(
                  axiosAuth,
                  queryClient,
                  id,
                  siteGeometry?.id
                );
                addNotification("The site was edited successfully", true);
                onClose();
              },
              onError: (error) => {
                const errs = parseBackendValidationErrors(error);
                if (errs !== null) {
                  const rawMsg =
                    errs.polygon ??
                    errs.detail ??
                    errs.non_field_errors ??
                    (Object.keys(errs).length > 0
                      ? Object.values(errs)[0]
                      : "Invalid polygon");
                  const polygonMsg = toUserFriendlyPolygonError(rawMsg);
                  setFormErrors((prev) => ({ ...prev, polygon: polygonMsg }));
                } else {
                  console.error("Failed to edit site geometry", error);
                  addNotification("Failed to edit the site geometry", false);
                }
                setIsSubmitting(false);
              },
            });
          } else {
            addNotification("The site was edited successfully", true);
            onClose();
          }
        },
        onError: (error) => {
          const errs = parseBackendValidationErrors(error);
          if (errs) {
            setFormErrors(errs);
          } else {
            console.error("Failed to edit site", error);
            addNotification("Failed to edit the site", false);
          }
          setIsSubmitting(false);
        },
      }
    );
  };

  const isSaving = isSubmitting || isPending || isSavingGeometry;

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const sourceOptions = [
    "utility defined",
    "calculated",
    "Early stage projects",
    "Asset Registry",
    "COD Tracker",
    "SSI Constr Early Stage",
    "SSI Operational registry",
    "M&A operations",
    "utility fleet",
    "TransAlta operations",
    "ECHO Residential",
    "ASD Residential",
    "Deriva Energy",
    "X-ELIO",
    "BEP US",
  ];

  const polygonMapInitialCenter = getMapCenterFromSite(
    formData.latitude || siteData?.latitude,
    formData.longitude || siteData?.longitude
  );
  const serverMapPolygon = getServerMapPolygon(siteGeometry);
  const mapPreviewPolygon = polygonFieldEdited
    ? formData.polygon
    : formData.polygon.trim() || serverMapPolygon;
  const isSyntheticGeometry =
    Boolean(siteData?.sitegeometry_synthetic) ||
    Boolean(siteGeometry?.synthetic);
  const isSyntheticMapOverlay =
    isSyntheticGeometry && !polygonFieldEdited;
  const expectsMapGeometry = Boolean(siteData?.sitegeometry);
  const isMapPreviewLoading =
    open &&
    (isRefreshingModalData ||
      (expectsMapGeometry &&
        !mapPreviewPolygon &&
        (isLoadingSiteData ||
          isFetchingSiteData ||
          isLoadingSiteGeometry ||
          isFetchingSiteGeometry)));
  const polygonMapKey = `geom-${siteGeometry?.id ?? "none"}-${mapRenderVersion}-${mapPreviewPolygon ? "ready" : "pending"}`;
  const syntheticGeometryMessage =
    "The displayed polygon may be replaced by hawkeye data pipelines.";
  const hasValidResetCoordinates =
    isValidLatitude(formData.latitude) && isValidLongitude(formData.longitude);
  const resetCoordinatesChanged =
    hasValidResetCoordinates &&
    haveCoordinatesChanged(
      formData.latitude,
      formData.longitude,
      siteData?.latitude,
      siteData?.longitude
    );

  const handleOpenResetGeometryModal = () => {
    if (!hasValidResetCoordinates) {
      setFormErrors((prev) => {
        const next = { ...prev };
        if (!isValidLatitude(formData.latitude)) {
          next.latitude =
            "A valid latitude is required before resetting geometry.";
        }
        if (!isValidLongitude(formData.longitude)) {
          next.longitude =
            "A valid longitude is required before resetting geometry.";
        }
        return next;
      });
      addNotification(
        "Enter valid latitude and longitude before resetting geometry",
        false
      );
      return;
    }

    setIsResetGeometryModalOpen(true);
  };

  const handleResetGeometry = () => {
    if (!hasValidResetCoordinates) {
      setIsResetGeometryModalOpen(false);
      addNotification(
        "Enter valid latitude and longitude before resetting geometry",
        false
      );
      return;
    }

    resetSiteGeometry(
      {
        siteId: id,
        siteGeometryId: siteGeometry?.id,
        siteGeometryUrl: siteData?.sitegeometry,
        ...(resetCoordinatesChanged
          ? {
              latitude: Number(formData.latitude),
              longitude: Number(formData.longitude),
            }
          : {}),
      },
      {
        onSuccess: (resetData) => {
          const polygonValue = polygonFromApi(resetData?.geometry?.poly) ?? "";

          setPolygonFieldEdited(false);
          setInitialPolygonWkt(polygonValue);
          setFormData((prev) => ({
            ...prev,
            polygon: polygonValue,
            latitude: resetData?.site?.latitude ?? prev.latitude,
            longitude: resetData?.site?.longitude ?? prev.longitude,
          }));
          setMapRenderVersion((version) => version + 1);
          setFormErrors({});
          setIsResetGeometryModalOpen(false);
          addNotification(
            resetData.coordinatesSaved
              ? "Coordinates saved and site geometry was reset successfully"
              : "The site geometry was reset successfully",
            true
          );
        },
        onError: (error) => {
          const validationErrors = parseBackendValidationErrors(error);
          if (validationErrors) {
            setFormErrors((prev) => ({ ...prev, ...validationErrors }));
          } else {
            console.error("Failed to reset site geometry", error);
          }
          addNotification("Failed to reset the site geometry", false);
        },
      }
    );
  };

  const renderMapPreview = (mapContainerHeight: string) => {
    if (isMapPreviewLoading) {
      return (
        <Box
          sx={{
            height: mapContainerHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "action.hover",
            color: "text.secondary",
            borderRadius: 1,
          }}
        >
          Loading map...
        </Box>
      );
    }

    return (
      <PolygonMap
        key={polygonMapKey}
        polygonData={mapPreviewPolygon}
        siteName={formData.name || siteData?.name}
        mapContainerHeight={mapContainerHeight}
        initialCenter={polygonMapInitialCenter}
        syntheticGeometry={isSyntheticMapOverlay}
        overlayCenter={
          isSyntheticMapOverlay ? polygonMapInitialCenter : undefined
        }
      />
    );
  };

  const modalPaperSx = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "96%", sm: "92%" },
    maxWidth: EDIT_SITE_MODAL_MAX_WIDTH,
    maxHeight: `${EDIT_SITE_MODAL_VH}vh`,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 1,
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={isSaving}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          outline: "none",
        }}
      >
        <Box sx={modalPaperSx}>
          {(isLoadingSiteData && !siteData) ||
          (isLoadingPortfoliosData && !PortfoliosData) ? (
            // Loading message while data is being fetched
            <Typography variant="h6" sx={{ p: 4, textAlign: "center" }}>
              Loading site data...
            </Typography>
          ) : (
            <>
              <Paper
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingX: 4,
                  paddingY: 2,
                }}
              >
                {formData.sam_id ? (
                  <Typography
                    variant="h5"
                    sx={{
                      marginTop: 1,
                      fontWeight: "bold",
                    }}
                  >
                    {formData.sam_id}
                  </Typography>
                ) : (
                  <Typography
                    variant="h5"
                    sx={{
                      marginTop: 1,
                      fontWeight: "bold",
                    }}
                  >
                    EDIT SITE
                  </Typography>
                )}

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton onClick={handleClose} disabled={isSaving}>
                    <HighlightOffOutlinedIcon />
                  </IconButton>
                </Box>
              </Paper>

              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  flexDirection: { xs: "column", md: "row" },
                  minHeight: 0,
                  alignItems: "stretch",
                  maxHeight: {
                    md: `calc(${EDIT_SITE_MODAL_VH}vh - ${EDIT_SITE_BODY_BELOW_HEADER_PX}px)`,
                  },
                }}
              >
                <Box
                  component="form"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: { xs: "1 1 auto", md: "1 1 50%" },
                    minWidth: 0,
                    maxHeight: {
                      xs: "none",
                      md: `calc(${EDIT_SITE_MODAL_VH}vh - ${EDIT_SITE_FORM_SCROLL_TOP_PX}px)`,
                    },
                    p: 3,
                    pt: 2,
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, overflowY: "auto", pr: 0.5 }}>
                  <TextField
                  label="SAM ID *"
                  variant="outlined"
                  fullWidth
                  name="sam_id"
                  value={formData.sam_id}
                  onChange={handleChange}
                  error={!!formErrors.sam_id}
                  helperText={formErrors.sam_id}
                />
                  <TextField
                  label="Site Name *"
                  variant="outlined"
                  fullWidth
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />
                <TextField
                  label="City"
                  variant="outlined"
                  fullWidth
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  error={!!formErrors.city}
                  helperText={formErrors.city}
                />
                <TextField
                  label="State"
                  variant="outlined"
                  fullWidth
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  error={!!formErrors.state}
                  helperText={formErrors.state}
                />
                <TextField
                  label="Address"
                  variant="outlined"
                  fullWidth
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  error={!!formErrors.address}
                  helperText={formErrors.address}
                />
                <TextField
                  label="Latitude"
                  variant="outlined"
                  fullWidth
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  error={!!formErrors.latitude}
                  helperText={formErrors.latitude}
                />
                <TextField
                  label="Longitude"
                  variant="outlined"
                  fullWidth
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  error={!!formErrors.longitude}
                  helperText={formErrors.longitude}
                />
                <TextField
                  select
                  label="Portfolio *"
                  variant="outlined"
                  fullWidth
                  name="portfolio"
                  value={formData.portfolio}
                  onChange={handleChange}
                  error={!!formErrors.portfolio}
                  helperText={formErrors.portfolio}
                >
                  {PortfoliosData?.map(
                    (portfolio: { id: number; name: string }) => (
                      <MenuItem key={portfolio.id} value={portfolio.id}>
                        {portfolio.name}
                      </MenuItem>
                    )
                  )}
                </TextField>
                <TextField
                  label="QF Docket ID"
                  variant="outlined"
                  fullWidth
                  name="docket_id"
                  value={formData.docket_id}
                  onChange={handleChange}
                  error={!!formErrors.docket_id}
                  helperText={formErrors.docket_id}
                />
                <TextField
                  label="Polygon (GeoJSON or WKT)"
                  variant="outlined"
                  fullWidth
                  name="polygon"
                  value={formData.polygon}
                  onChange={handleChange}
                  error={!!formErrors.polygon}
                  helperText={
                    formErrors.polygon ||
                    "Paste GeoJSON or WKT; the map preview updates as you type"
                  }
                />
                {isSyntheticGeometry && (
                  <Alert severity="warning">{syntheticGeometryMessage}</Alert>
                )}
                {!isWideLayout && (
                  <Box sx={{ pt: 0.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Map preview
                    </Typography>
                    {renderMapPreview("360px")}
                  </Box>
                )}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="DC kW *"
                    variant="outlined"
                    fullWidth
                    name="dc_kw"
                    value={formData.dc_kw}
                    onChange={handleChange}
                    error={!!formErrors.dc_kw}
                    helperText={formErrors.dc_kw}
                  />
                  <TextField
                    label="AC kW *"
                    variant="outlined"
                    fullWidth
                    name="ac_kw"
                    value={formData.ac_kw}
                    onChange={handleChange}
                    error={!!formErrors.ac_kw}
                    helperText={formErrors.ac_kw}
                  />
                </Box>
                <TextField
                  select
                  label="AC Source"
                  variant="outlined"
                  fullWidth
                  name="ac_source"
                  value={formData.ac_source}
                  onChange={handleChange}
                  error={!!formErrors.ac_source}
                  helperText={formErrors.ac_source}
                >
                  {sourceOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="DC Source"
                  variant="outlined"
                  fullWidth
                  name="dc_source"
                  value={formData.dc_source}
                  onChange={handleChange}
                  error={!!formErrors.dc_source}
                  helperText={formErrors.dc_source}
                >
                  {sourceOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    pt: 2,
                    pb: 0.5,
                    mt: 2,
                    backgroundColor: "background.paper",
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  {isSaving && <LinearProgress sx={{ mb: 2 }} />}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                  <CustomButton
                    sx={{
                      paddingY: 0.5,
                    }}
                    variant="outlined"
                    color="secondary"
                    label="Cancel"
                    disabled={isSaving}
                    onClick={handleClose}
                  />

                  <Box sx={{ display: "flex", gap: 2 }}>
                    <CustomButton
                      disabled={
                        isSaving ||
                        isLoadingSites ||
                        isResettingGeometry
                      }
                      sx={{
                        paddingY: 0.5,
                      }}
                      variant="outlined"
                      color="secondary"
                      label="Reset Geometry"
                      onClick={handleOpenResetGeometryModal}
                    />

                    <CustomButton
                      disabled={
                        isSaving ||
                        isLoadingSites ||
                        isResettingGeometry
                      }
                      sx={{
                        paddingY: 0.5,
                      }}
                      variant="contained"
                      color="primary"
                      label={isSaving ? "Saving..." : "Save Site"}
                      onClick={handleSubmit}
                    />
                  </Box>
                  </Box>
                </Box>
                </Box>

                {isWideLayout && (
                  <Box
                    sx={{
                      flex: "0 0 46%",
                      minWidth: 340,
                      maxWidth: 560,
                      borderLeft: 1,
                      borderColor: "divider",
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "grey.50",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: "min(52vh, 520px)",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600, flexShrink: 0 }}
                    >
                      Map preview
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                      {renderMapPreview("min(52vh, 480px)")}
                    </Box>
                  </Box>
                )}
              </Box>
              <ConfirmResetGeometryModal
                open={isResetGeometryModalOpen}
                onClose={() => setIsResetGeometryModalOpen(false)}
                onReset={handleResetGeometry}
                isLoading={isResettingGeometry}
                willSaveCoordinates={resetCoordinatesChanged}
              />
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default EditNewSiteModal;
